import { useRouter } from 'next/router'
import { useState } from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'
import { Icon } from '@wartek-id/icon-v2'

import PanelPerilakuKerja from 'app/KinerjaPage/components/PanelPerilakuKerja'
import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import DialogSignSKP from './DialogSignSKP'
import {
  useAPIDeleteKinerja,
  useAPIUpdateState,
} from 'api/kinerja/useAPIKinerja'
import useSKP from 'app/KinerjaPage/hooks/useSKP'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'
import StickyFooter from 'app/KinerjaPage/components/StickyFooter'
import DialogDeleteSKP from './DialogDeleteSKP'
import TabContentHeader from './TabContentHeader'
import useToggleSdkContext from 'utils/useToogleSdk'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'

import type { Kinerja } from 'types/kinerja'

interface TabPerilakuKerjaProps {
  skp: Kinerja
  isDeleteSKPEnabled?: boolean
  showHeader?: boolean
  className?: string
}

const TabPerilakuKerjaApproved = ({
  skp,
  isDeleteSKPEnabled = false,
  showHeader = true,
  className,
}: TabPerilakuKerjaProps) => {
  const router = useRouter()
  const toast = useToast()
  const { trackEvent } = useSKPTrackEvent()
  const { getAspekPerilakuKerjaById, role } = useContentContext()

  const baseUrl = usePengelolaanKinerjaBaseUrl()
  const toggles = useToggleSdkContext()
  const isSPNFEnabled = toggles?.['kinerja-spnf-enabled'] === true

  const { mutate: signSKP, isLoading: isSigningSKP } = useAPIUpdateState()
  const { mutate: deleteSKP, isLoading: isDeletingSKP } = useAPIDeleteKinerja()

  const [isOpenDialogSignSKP, setIsOpenDialogSignSKP] = useState(false)
  const [isOpenDialogDeleteSKP, setIsOpenDialogDeleteSKP] = useState(false)

  const { getChangesPerilakuKerja } = useSKP(skp)

  const handleClickApprove = () => {
    setIsOpenDialogSignSKP(true)
  }

  const handleClickConfirm = () => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PLANNING_AGREEMENT',
      screen_name: 'KINERJA_PLANNING_AGREEMENT_POP_UP',
      kinerja_planning_id: skp?.id,
      kinerja_planning_status: skp?.status,
    })

    signSKP(
      {
        skpId: skp.id,
        ...(isSPNFEnabled ? { role } : {}),
      },
      {
        onError() {
          toast({
            message: 'Gagal menyepakati SKP',
          })
        },
        onSuccess() {
          router.push(baseUrl)
        },
      }
    )
  }

  const handleClickDelete = () => {
    deleteSKP(
      {},
      {
        onError() {
          toast({
            message: 'Perencanaan gagal dihapus. Coba lagi.',
          })
        },
        onSuccess() {
          setIsOpenDialogDeleteSKP(false)
          toast({
            icon: <Icon color="inverse">check</Icon>,
            message: 'Perencanaan berhasil dihapus!',
          })
          router.push(baseUrl)
        },
      }
    )
  }

  return (
    <>
      <div className={clsx('p-9', className)}>
        <div className="flex flex-col gap-9">
          {skp.status === 'APPROVED' && showHeader && <TabContentHeader />}

          {skp.reviewPrilakuKerja.map((perilakuKerja) => {
            const aspek = getAspekPerilakuKerjaById(perilakuKerja.aspekId)
            const {
              originalIndikator,
              indikator,
              originalPerwujudanPerilaku,
              perwujudanPerilaku,
            } = getChangesPerilakuKerja(perilakuKerja)

            return (
              <PanelPerilakuKerja
                key={`PanelPerilakuKerja-${aspek.id}`}
                data-testid={`PanelPerilakuKerja-${aspek.id}`}
                title={aspek?.value}
                originalIndikatorPerilaku={originalIndikator}
                indikatorPerilaku={indikator}
                originalPerwujudanPerilaku={originalPerwujudanPerilaku}
                perwujudanPerilaku={perwujudanPerilaku}
              />
            )
          })}
        </div>
      </div>

      <StickyFooter>
        {skp.status === 'APPROVED' ? (
          <Button
            size="lg"
            onClick={() => handleClickApprove()}
            type="button"
            data-testid="btn-back-to-home"
          >
            Sepakati Perencanaan
          </Button>
        ) : (
          <Button
            size="lg"
            onClick={() => {
              router.push(baseUrl)
            }}
            type="button"
            data-testid="btn-back-to-home"
          >
            Kembali ke Beranda
          </Button>
        )}
      </StickyFooter>

      <DialogSignSKP
        data-testid="dialog-sign-skp"
        isDeleteSKPEnabled={isDeleteSKPEnabled}
        ekinEligible={skp?.ekinEligible}
        isOpen={isOpenDialogSignSKP}
        onClose={() => setIsOpenDialogSignSKP(false)}
        isLoading={isSigningSKP}
        onClickCancel={() => {
          setIsOpenDialogSignSKP(false)

          if (isDeleteSKPEnabled) {
            setIsOpenDialogDeleteSKP(true)
          }
        }}
        onClickConfirm={handleClickConfirm}
      />

      <DialogDeleteSKP
        data-testid="dialog-delete-skp"
        isOpen={isOpenDialogDeleteSKP}
        onClose={() => setIsOpenDialogDeleteSKP(false)}
        isLoading={isDeletingSKP}
        onClickCancel={() => {
          setIsOpenDialogDeleteSKP(false)
          setIsOpenDialogSignSKP(true)
        }}
        onClickConfirm={handleClickDelete}
      />
    </>
  )
}

export default TabPerilakuKerjaApproved
